import React from 'react';
import { useTranslation } from 'react-i18next';
import { 
  ExclamationTriangleIcon,
  ClockIcon,
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
} from '@heroicons/react/24/outline';
import { useVersionNavigation } from '../hooks/useVersioning';
import Tippy from '@tippyjs/react';

interface VersionNavigationProps {
  budgetId: number;
}

export const VersionNavigation: React.FC<VersionNavigationProps> = ({ budgetId }) => {
  const { t } = useTranslation();
  const {
    versionInfo,
    isHistorical,
    undo,
    redo,
    canUndo,
    canRedo,
    isNavigating,
  } = useVersionNavigation(budgetId);

  // Always render undo/redo buttons, even if disabled
  // Only hide if there are no versions at all (shouldn't happen in normal flow)
  if (versionInfo.totalVersions === 0) {
    return null;
  }

  const formatTimestamp = (timestamp?: Date) => {
    if (!timestamp) return '';
    
    const now = new Date();
    const diff = now.getTime() - timestamp.getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 1) return t('versioning.justNow');
    if (minutes < 60) return t('versioning.minutesAgo', { count: minutes });
    if (hours < 24) return t('versioning.hoursAgo', { count: hours });
    return t('versioning.daysAgo', { count: days });
  };

  return (
    <div className="flex items-center space-x-2">
      {/* Undo/Redo buttons first */}
      <div className="flex items-center space-x-1">
        <Tippy
          content={t('versioning.undo')}
          disabled={!canUndo || isNavigating}
          placement="top"
          delay={[300, 0]}
          className="bg-slate-900 text-white text-xs rounded-md shadow-lg px-2 py-1 dark:bg-surface-700"
        >
          <button
            onClick={undo}
            disabled={!canUndo || isNavigating}
            className="rounded p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-slate-400 dark:hover:bg-surface-700 dark:hover:text-slate-200 dark:disabled:hover:bg-transparent dark:disabled:hover:text-slate-400"
            aria-label={t('versioning.undo')}
          >
            <ArrowUturnLeftIcon className="h-4 w-4" />
          </button>
        </Tippy>

        <Tippy
          content={t('versioning.redo')}
          disabled={!canRedo || isNavigating}
          placement="top"
          delay={[300, 0]}
          className="bg-slate-900 text-white text-xs rounded-md shadow-lg px-2 py-1 dark:bg-surface-700"
        >
          <button
            onClick={redo}
            disabled={!canRedo || isNavigating}
            className="rounded p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-slate-400 dark:hover:bg-surface-700 dark:hover:text-slate-200 dark:disabled:hover:bg-transparent dark:disabled:hover:text-slate-400"
            aria-label={t('versioning.redo')}
          >
            <ArrowUturnRightIcon className="h-4 w-4" />
          </button>
        </Tippy>
      </div>

      {/* Version info second - show if there's at least one version */}
      {versionInfo.totalVersions > 0 && (
        <div className="flex items-center space-x-1 text-xs text-slate-500 dark:text-slate-400">
          <ClockIcon className="h-3 w-3" />
          <span>
            {versionInfo.totalVersions === 1 
              ? t('versioning.currentVersion', { version: versionInfo.currentVersionIndex + 1 })
              : t('versioning.versionInfo', {
                  current: versionInfo.currentVersionIndex + 1,
                  total: versionInfo.totalVersions,
                })
            }
          </span>
          {versionInfo.currentVersionTimestamp && (
            <span className="text-slate-400 dark:text-slate-500">
              • {formatTimestamp(versionInfo.currentVersionTimestamp)}
            </span>
          )}
        </div>
      )}

      {/* Historical version warning last */}
      {isHistorical && (
        <Tippy
          content={
            <div className="space-y-2 text-xs">
              <div className="font-medium">{t('versioning.historicalVersionTitle')}</div>
              <div>{t('versioning.historicalVersionMessage')}</div>
              <div className="text-slate-300">{t('versioning.historicalVersionHint')}</div>
            </div>
          }
          placement="top"
          delay={[300, 0]}
          maxWidth={300}
          interactive={false}
          className="bg-slate-900 text-white text-xs rounded-md shadow-lg px-3 py-2 dark:bg-surface-700"
        >
          <div className="flex items-center space-x-1 rounded-md bg-slate-100 px-2 py-1 text-xs text-slate-700 dark:bg-surface-700 dark:text-slate-300">
            <ExclamationTriangleIcon className="h-3 w-3" />
            <span>{t('versioning.viewingHistorical')}</span>
          </div>
        </Tippy>
      )}

    </div>
  );
};